import { ReactNode } from "react";
import { Flex } from "@chakra-ui/react";
import { useFiltersUrlQuery } from "components/Shared/FilterEditor/urlQuerySerializers";
import { BrandRetailerFilter, RetailerType } from "__generated__/gql/graphql";
import FilterBarWithSearch from "components/Shared/FilterEditor/FilterBar/FilterBarWithSearch";
import type { AppliedFilter } from "components/Shared/FilterEditor/utils";
import RetailerTypeCheckboxes, {
  getValueFromFilters as getRetailerTypeValueFromFilters,
  makeFiltersHandleChange as makeFiltersHandleRetailerTypeChange,
  RETAILER_TYPE_FILTER_ID,
} from "components/Shared/RetailerDirectory/RetailerTypeCheckboxes";
import RetailersTable, { Props as TableProps } from "./Table";
import filterDefinitions from "./filterDefinitions";
import StatusFilterTabs, {
  STATUS_TABS_FILTER_NAME,
  StatusFilter,
} from "./StatusFilterTabs";

const RETAILER_FILTER_PREFIX = "retailer";

const DEFAULT_FILTERS: AppliedFilter[] = [
  {
    filter: STATUS_TABS_FILTER_NAME,
    value: StatusFilter.ACTIVE,
    variant: "equalTo",
  },
  {
    filter: `${RETAILER_FILTER_PREFIX}__${RETAILER_TYPE_FILTER_ID}`,
    value: [RetailerType.CHAIN, RetailerType.AFFILIATION],
    variant: "in",
  },
];

type Props = TableProps & {
  headerRight?: ReactNode;
};

export default function RetailersTableView({ headerRight, ...rest }: Props) {
  const { filter, filters, isTableReady, handleFiltersChange } =
    useFiltersUrlQuery<BrandRetailerFilter>(filterDefinitions, {
      defaultFilters: DEFAULT_FILTERS,
    });

  return (
    <>
      <FilterBarWithSearch
        key={String(isTableReady)}
        filterDefinitions={filterDefinitions}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        headerRight={
          <Flex
            alignItems="center"
            px={4}
            flex="1 1 auto"
            justifyContent="space-between"
            gap={4}
          >
            <StatusFilterTabs
              filters={filters}
              handleFiltersChange={handleFiltersChange}
            />

            <RetailerTypeCheckboxes
              size="sm"
              value={getRetailerTypeValueFromFilters(
                filters,
                RETAILER_FILTER_PREFIX,
              )}
              onChange={makeFiltersHandleRetailerTypeChange(
                filters,
                handleFiltersChange,
                RETAILER_FILTER_PREFIX,
              )}
              listProps={{ lineHeight: "1" }}
            />

            {headerRight}
          </Flex>
        }
      />
      <RetailersTable {...rest} filter={filter} isReady={isTableReady} />
    </>
  );
}
